import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pdApi } from '../api';
import './QuickStartPreview.css';
/* The helpers, the chips and the proposal tree all live with the shared body now — the
   source keeps them in the partial both screens include. */
import {
  Chip, PHASE_LABEL_FULL, PHASE_ORDER, QuickStartReviewBody, computeLocks, dateRange,
  dropKey, keyDel, keyGlobal, keyOpp, keyProg, keyTeam, n, plural, s, type Dict,
} from './QuickStartReviewBody';

/**
 * Program Quick Start — Analysis & Preview.
 *
 * BUILT TO `docs/program-quick-start-analysis-preview-design-spec.md`, Parts 1–9. That
 * document is self-contained and authoritative; where this file makes a choice the spec did
 * not dictate, it says so.
 *
 * ── THE SCREEN'S JOB IS TRIAGE (spec Part 1) ────────────────────────────────────────────
 *
 * A reviewer faces AI output covering ~170 opportunities and up to ~1,700 work items and has
 * to decide what is real. Everything here serves scanning speed: three-level containment
 * shown structurally, every attribute as a chip, and selection as the primary interaction.
 *
 * ── ONE DELIBERATE DEPARTURE ────────────────────────────────────────────────────────────
 *
 * Spec Part 6 lists four Materialize 1.0.0 collisions to work around. THIS PLATFORM DOES NOT
 * LOAD MATERIALIZE, so those collisions do not exist and their `!important` armour is not
 * reproduced — the visual result is identical, which is what the spec actually specifies.
 * Marked at each site in the stylesheet.
 *
 * ── WHAT IS NOT HERE YET, AND WHY ───────────────────────────────────────────────────────
 *
 * Duplicate resolution (spec 4.12) needs the server's `dupWarn` payload on the load response,
 * which this port does not yet return. The markup and styling follow the spec so it can be
 * switched on when that lands; until then the state simply does not occur. Stated here rather
 * than left to be discovered.
 *
 * The analysing/poll state (5.2) IS here — `/quick-start/run/:id/status` and background
 * analysis landed with the change record's §3.10.
 */
export function QuickStartPreview() {
  const [runs, setRuns] = useState<Dict[]>([]);
  const [run, setRun] = useState<Dict | null>(null);
  const [proposal, setProposal] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: string; text: string } | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  /* Seconds since the analysis started, for the live elapsed readout. A 2-5 minute wait with
     a static message gives no sign that anything is happening — change record §8 item 3. */
  const [elapsed, setElapsed] = useState(0);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /* Reference files, and the operator's note for each — keyed BY FILENAME, which is how the
     server pairs them back up. Keeping the same key on both sides is what stops a note being
     attached to the wrong file when one is rejected or the order differs. */
  const refRef = useRef<HTMLInputElement | null>(null);
  const [refFiles, setRefFiles] = useState<File[]>([]);
  const [refNotes, setRefNotes] = useState<Record<string, string>>({});

  /*
   * Duplicate warnings from the server, and the reviewer's decision for each flagged record.
   *
   * The server RECOMPUTES the merge target at generate time and never trusts the client for a
   * record id — this map carries only the CHOICE ('merge' | 'new' | 'skip'), keyed by ref. A
   * stale review screen can therefore pick the wrong verb, but it can never merge into the
   * wrong project.
   */
  const [dupWarn, setDupWarn] = useState<{
    programs: Record<string, Dict>;
    opportunities: Record<string, Dict>;
  }>({ programs: {}, opportunities: {} });
  const [resolutions, setResolutions] = useState<Record<string, string>>({});

  const [openRuns, setOpenRuns] = useState<Set<number>>(new Set());
  const [sel, setSel] = useState<Set<string>>(new Set());

  /* §19.2 — the records that genuinely exist in the database, keyed exactly as the
     selection is. Each locks only itself; see the load handler for why. */
  const [genProg, setGenProg] = useState<Set<string>>(new Set());
  const [genOpp, setGenOpp] = useState<Set<string>>(new Set());

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  /*
   * WHICH RUN IS OPEN LIVES IN THE URL — `?run=8` — exactly as the source does it.
   *
   * The source's Open review is `<a href="…/quick-start?run=8">`: a real navigation. That is
   * not incidental, and reproducing it as a bare state update is what made the button appear to
   * do nothing. The proposal loaded correctly and rendered BELOW the run list, but the viewport
   * never moved, so the screen looked identical and the click read as dead.
   *
   * Putting the run in the URL restores what the link gave for free: the address reflects what
   * you are looking at, reload and bookmark work, and Back returns to the run list. The scroll
   * to the top of the body is what the source got from loading a fresh page.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const runParam = searchParams.get('run');
  const bodyRef = useRef<HTMLDivElement | null>(null);

  /* ---- load ---- */
  const load = useCallback(async (runId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = (await pdApi.quickStart.load(runId)) as Dict;
      setRuns((data.runs as Dict[]) ?? []);
      setRun((data.run as Dict) ?? null);
      const p = (data.proposal as Dict) ?? null;
      setProposal(p);
      const dw = (data.dupWarn as { programs?: Record<string, Dict>; opportunities?: Record<string, Dict> }) ?? null;
      setDupWarn({ programs: dw?.programs ?? {}, opportunities: dw?.opportunities ?? {} });
      setResolutions({});

      /*
       * NOTHING IS PRE-TICKED, AT ANY LEVEL — implementation spec 6.7.
       *
       * This reverses what this screen did before, and the spec states it as a rule precisely
       * because the instinct when adding a list is to default it on. That instinct is wrong
       * here: a proposal can hold 90 projects, 172 opportunities and thousands of work items
       * read out of a document by a model, and defaulting them on means one click GENERATES
       * EVERYTHING — including whatever the extraction got wrong. The reviewer's job is to say
       * what is real, and an untouched screen has said nothing.
       *
       * The server's selection map is opt-OUT (`selection[key] === false` skips), and that
       * still works unchanged: the map is computed from this set at generate time, so an empty
       * set simply sends `false` for everything. No contract had to be inverted — only the
       * starting state.
       *
       * Generated records are the one exception, and they are not really an exception: those
       * ticks are not a selection, they are a record of what exists. §19.2 keys that off the
       * records themselves rather than the run's status — on a run that generated nothing the
       * sets are empty, so this is the same empty start as before with no branch to get wrong.
       */
      const { genProg: gProg, genOpp: gOpp } = computeLocks(
        (p?.programs as Dict[]) ?? [],
        data.createdIds as { project?: string[]; opportunity?: string[] } | undefined,
      );
      setGenProg(gProg);
      setGenOpp(gOpp);
      setSel(new Set([...gProg, ...gOpp]));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  /*
   * The URL drives the load. `?run=8` opens that run; no param loads the history only. Keyed on
   * `runParam` so pressing Back — which changes the URL and nothing else — reloads correctly.
   */
  useEffect(() => {
    void (async () => {
      await load(runParam ? Number(runParam) : undefined);
      /* What the source got from a page load: the proposal is at the top of the view, not
         somewhere below the run list the user was just looking at. */
      if (runParam && bodyRef.current) bodyRef.current.scrollTop = 0;
    })();
  }, [load, runParam]);

  /* The shell never bounds its content area, so the height is measured — CLAUDE.md, "A
     screen header never scrolls". Callback ref because the body mounts behind a gate. */
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      setRootH((p) => {
        const next = Math.max(360, Math.floor(window.innerHeight - top - inset));
        return p !== null && Math.abs(p - next) < 2 ? p : next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const programs = useMemo(() => (proposal?.programs as Dict[]) ?? [], [proposal]);
  const assumptions = useMemo(() => (proposal?.assumptions as string[]) ?? [], [proposal]);
  /* Whether ANYTHING was generated — the only thing that is still a run-level question,
     because Delete generated data operates on the run. It is NOT a lock. */
  const anyGenerated = genProg.size > 0 || genOpp.size > 0;

  /* ---- selection ---- */
  const isOn = (k: string) => sel.has(k);

  const setMany = (keys: string[], on: boolean) =>
    setSel((cur) => {
      const next = new Set(cur);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  const descendantsOf = useCallback(
    (pg: Dict) => {
      const pid = s(pg.ref || pg.code);
      const out = [keyProg(pid)];
      for (const o of (pg.opportunities as Dict[]) ?? []) {
        const oid = s(o.ref || o.code);
        out.push(keyOpp(pid, oid));
        const dels = ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
        dels.forEach((_d, i) => out.push(keyDel(pid, oid, String(i))));
      }
      for (const t of (pg.team as Dict[]) ?? []) out.push(keyTeam(pid, s(t.ref || t.name)));
      ((pg.global_deliverables as Dict[]) ?? []).forEach((_g, gi) =>
        out.push(keyGlobal(pid, String(gi))),
      );
      return out;
    },
    [],
  );

  const toggleProgram = (pg: Dict, on: boolean) => setMany(descendantsOf(pg), on);

  const toggleOpp = (pg: Dict, o: Dict, on: boolean) => {
    const pid = s(pg.ref || pg.code);
    const oid = s(o.ref || o.code);
    const dels = ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
    const keys = [keyOpp(pid, oid), ...dels.map((_d, i) => keyDel(pid, oid, String(i)))];
    // Checking an opportunity back-fills its program; unchecking never clears upward.
    if (on) keys.push(keyProg(pid));
    setMany(keys, on);
  };

  const toggleDel = (pg: Dict, o: Dict, idx: number, on: boolean) => {
    const pid = s(pg.ref || pg.code);
    const oid = s(o.ref || o.code);
    const keys = [keyDel(pid, oid, String(idx))];
    if (on) keys.push(keyOpp(pid, oid), keyProg(pid));
    setMany(keys, on);
  };

  /*
   * FILTER — implementation spec 6.4. A number is an ID and is matched EXACTLY; anything else
   * is a name search.
   *
   * The exactness is the part that matters, and it rules out two failures seen in real use:
   *
   *   PREFIX BLEED  typing 3639 must not also pull in 3631 and 3638 because they share a
   *                 prefix. A substring match on ids does exactly that.
   *   PROSE BLEED   typing 3639 must not match a DIFFERENT initiative whose write-up happens
   *                 to mention 3639. A search across descriptions does exactly that.
   *
   * So a purely numeric query is compared with `===` against the record's own code and nothing
   * else. A partial number (`363`) therefore matches nothing, which is correct: it is not an
   * id. Anything non-numeric falls back to a substring search over the record's own id and
   * name — never its description, for the prose-bleed reason above.
   */
  const [find, setFind] = useState('');
  const filter = useMemo(() => {
    const q = find.trim().toLowerCase();
    const numeric = /^[0-9][0-9a-z\-/.]*$/.test(q);
    const hit = (code: unknown, name: unknown) => {
      const c = s(code).toLowerCase();
      if (numeric) return c === q;
      return c.includes(q) || s(name).toLowerCase().includes(q);
    };
    return { q, hit };
  }, [find]);

  /*
   * Which programs and opportunities the filter leaves visible.
   *
   * A PROJECT match keeps ALL of its opportunities — you asked for the initiative, so you get
   * it whole. An OPPORTUNITY match keeps its project visible but shows only the matching
   * opportunities, so one initiative can be pulled out of a 90-project intake and worked on.
   */
  const visible = useMemo(() => {
    const progs = new Set<string>();
    const opps = new Set<string>();
    for (const pg of programs) {
      const pid = s(pg.ref ?? pg.code);
      const projHit = !filter.q || filter.hit(pg.code, pg.name);
      let any = projHit;
      for (const o of ((pg.opportunities as Dict[]) ?? [])) {
        const oid = s(o.ref ?? o.code);
        const on = projHit || (!!filter.q && filter.hit(o.code, o.name));
        if (on) {
          opps.add(keyOpp(pid, oid));
          any = true;
        }
      }
      if (any) progs.add(pid);
    }
    return { progs, opps };
  }, [programs, filter]);

  /*
   * SELECT ALL / CLEAR ALL ACT ON WHAT YOU CAN SEE — spec 6.4.
   *
   * Ticking rows hidden behind a filter would be invisible AND would then be generated, which
   * is the exact opposite of what a filter is for: you narrowed the screen to work on one
   * initiative, and the button quietly armed the other eighty-nine.
   */
  const selectAll = (on: boolean) => {
    /*
     * §19.2 — a generated record stays ticked through both of these. Its checkbox is
     * disabled AND checked because the tick is a record of what exists, not a selection;
     * letting Clear all untick it would leave a greyed-out empty box claiming the opposite
     * of the ✓ Generated chip beside it.
     */
    if (!on) {
      setSel(new Set([...genProg, ...genOpp]));
      return;
    }
    const keys: string[] = [...genProg, ...genOpp];
    for (const pg of programs) {
      const pid = s(pg.ref ?? pg.code);
      if (!visible.progs.has(pid)) continue;
      keys.push(keyProg(pid));
      for (const o of ((pg.opportunities as Dict[]) ?? [])) {
        const oid = s(o.ref ?? o.code);
        if (!visible.opps.has(keyOpp(pid, oid))) continue;
        keys.push(keyOpp(pid, oid));
        const dels = ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
        dels.forEach((_d, i) => keys.push(keyDel(pid, oid, String(i))));
      }
      for (const t of ((pg.team as Dict[]) ?? [])) keys.push(keyTeam(pid, s(t.ref ?? t.name)));
      ((pg.global_deliverables as Dict[]) ?? []).forEach((_g, gi) =>
        keys.push(keyGlobal(pid, String(gi))),
      );
    }
    setSel(new Set(keys));
  };

  /* Live counts for the dark bar. */
  const counts = useMemo(() => {
    let progs = 0;
    let opps = 0;
    let dels = 0;
    for (const k of sel) {
      if (k.startsWith('team:')) continue;
      const parts = k.split('|');
      if (parts.length === 1) progs++;
      else if (parts.length === 2) opps++;
      else dels++;
    }
    return { progs, opps, dels };
  }, [sel]);

  /* ------------------------------------------------------------------ *
   * Drag a work item to another phase — implementation spec 6.5.
   *
   * SCOPE: an item may only move WITHIN the opportunity (or the programme-level set) it
   * already belongs to. Dropping it under a different opportunity would re-assign the work,
   * which is a different decision entirely and not one a drag should be able to make.
   *
   * The spec's four performance rules are about hand-rolled DOM manipulation — no
   * document-wide query per event, measure once per phase, write only when the insertion
   * point changes. Holding the drag in React state satisfies all four by construction: the
   * only per-event work is comparing an index, and the only DOM writes are React's own, which
   * happen when that index actually changes.
   *
   * The move is applied OPTIMISTICALLY and confirmed with the server. On failure the previous
   * proposal is put back and the reason is shown — a silent revert would look like the drag
   * simply did not take.
   * ------------------------------------------------------------------ */
  const dragRef = useRef<{ pid: string; oid: string | null; index: number } | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);
  /*
   * WHERE inside the hovered phase the item lands — the array index of the row it goes BEFORE,
   * or null for the end. Without this a drag could only ever append, so an item could change
   * phase but never change position, and dropping onto the phase it already sits in did
   * nothing at all (source `quick-start.ejs:1341`, which computes the same reference row).
   */
  const [dropBefore, setDropBefore] = useState<number | null>(null);
  /*
   * `dragover` fires ~60 times a second and a programme can hold thousands of rows, so the row
   * midpoints are measured ONCE per phase per drag, not per event. The dragged row is excluded
   * from the measurement, so moving it cannot invalidate the numbers derived from it.
   */
  const rowsRef = useRef<{ el: Element; rows: { idx: number; mid: number }[] } | null>(null);
  /* ONE key builder for the hovered phase group. The handler and the two render sites derived
     this separately and disagreed on how to spell a null oid — so the programme-level groups
     never lit up, and a valid drop target looked like an invalid one. */

  /** The flat list a work item belongs to: an opportunity's, or the programme-level set. */
  const listFor = (pg: Dict, oid: string | null): Dict[] =>
    oid
      ? (((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[oid] ?? [])
      : ((pg.global_deliverables as Dict[]) ?? []);

  const onItemDragStart = (
    e: React.DragEvent,
    pid: string,
    oid: string | null,
    index: number,
  ) => {
    dragRef.current = { pid, oid, index };
    rowsRef.current = null;
    setDropBefore(null);
    /*
     * `setData` IS NOT OPTIONAL — this is what made the item draggable but not droppable.
     *
     * The HTML5 drag contract requires the drag to carry a payload: without a `setData` call
     * the drag starts and shows its ghost image, but no drop target will accept it, so the
     * item springs back and nothing happens. The source does exactly this
     * (`quick-start.ejs:818`) and the omission was mine.
     *
     * Wrapped in try/catch as the source wraps it — some browsers throw on `setData` outside a
     * genuine user-initiated drag, and a throw here would abort the drag entirely.
     */
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', 'wi');
    } catch {
      /* non-fatal — see above */
    }
  };
  const onItemDragEnd = () => {
    dragRef.current = null;
    rowsRef.current = null;
    setDropHint(null);
    setDropBefore(null);
  };

  /** A drop is legal only inside the SAME owner — same project, same opportunity. */
  const canDropHere = (pid: string, oid: string | null) => {
    const d = dragRef.current;
    return !!d && d.pid === pid && (d.oid ?? '') === (oid ?? '');
  };

  const onPhaseDragOver = (e: React.DragEvent, pid: string, oid: string | null, pk: string) => {
    if (!canDropHere(pid, oid)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const hint = dropKey(pid, oid, pk);

    /* Measurements belong to ONE phase — a new phase invalidates them. */
    const grp = e.currentTarget as HTMLElement;
    if (!rowsRef.current || rowsRef.current.el !== grp) {
      const rows: { idx: number; mid: number }[] = [];
      grp.querySelectorAll<HTMLElement>('[data-qsidx]').forEach((el) => {
        const idx = Number(el.dataset.qsidx);
        /* The dragged row itself is never a reference point — `data-qsidx` is its position in
           the owner's flat list, so it identifies the row uniquely across all five phases. */
        if (idx === dragRef.current?.index) return;
        const b = el.getBoundingClientRect();
        rows.push({ idx, mid: b.top + b.height / 2 });
      });
      rowsRef.current = { el: grp, rows };
    }
    let before: number | null = null;
    for (const r of rowsRef.current.rows) {
      if (e.clientY < r.mid) {
        before = r.idx;
        break;
      }
    }
    /* Only write when the insertion point actually changes — rule 3 of the source's four. */
    if (before !== dropBefore) setDropBefore(before);
    /* Only when it actually changes — spec 6.5 rule 3. */
    if (dropHint !== hint) setDropHint(hint);
  };

  const onPhaseDrop = async (
    e: React.DragEvent,
    pid: string,
    oid: string | null,
    pk: string,
  ) => {
    /* The drop's own preventDefault, as the source has it (`quick-start.ejs:893`). Without it
       the browser runs its default action for the dropped payload — which for `text/plain` can
       mean navigating away from the page. */
    e.preventDefault();

    /*
     * THE GUARD IS EVALUATED BEFORE THE DRAG STATE IS CLEARED. This ordering was the bug that
     * made a drop do nothing at all.
     *
     * It used to read:
     *     const d = dragRef.current;
     *     onItemDragEnd();                            // sets dragRef.current = null
     *     if (!d || !canDropHere(pid, oid)) return;   // canDropHere RE-READS the null ref
     *
     * `canDropHere` resolves `dragRef.current` itself, so clearing the ref first made it
     * return false on every drop — the item dragged, the target highlighted, and then the
     * handler returned before touching anything. Capturing `d` was not enough, because the
     * guard did not use `d`.
     *
     * Now the decision is made from the captured value, and the cleanup happens after it.
     */
    const d = dragRef.current;
    const sameOwner = !!d && d.pid === pid && (d.oid ?? '') === (oid ?? '');
    const before = dropBefore; /* captured for the same reason `d` is — cleanup clears it */
    onItemDragEnd();
    if (!d || !sameOwner) return;

    const pg = programs.find((x) => s(x.ref ?? x.code) === pid);
    if (!pg) return;
    const list = listFor(pg, oid);
    const moving = list[d.index];
    if (!moving) return;

    /*
     * Rebuild the WHOLE flat order, phase by phase in canonical order, with the dragged item
     * appended to its new phase. Sending the whole list is the spec's rule: the screen shows a
     * grouped view of a flat array, and index arithmetic across that boundary is where an
     * off-by-one silently re-phases the wrong item.
     */
    const phaseOf = (item: Dict) => s(item.phase_key ?? item.phase) || 'Assessment_Planning';
    const order: { index: number; phase: string }[] = [];
    const groupKeys = [
      ...PHASE_ORDER,
      ...list.map(phaseOf).filter((k) => !PHASE_ORDER.includes(k)),
    ];
    for (const key of [...new Set(groupKeys)]) {
      list.forEach((item, i) => {
        if (i === d.index) return;
        if (phaseOf(item) === key) order.push({ index: i, phase: key });
      });
      if (key !== pk) continue;
      /*
       * Insert AT the reference row rather than appending. `before` is the flat-array index of
       * the row the pointer sits above; splicing at its position in the rebuilt list keeps the
       * two views of the same data in step. A null reference means the pointer was below every
       * row, which is the end of the phase.
       */
      if (before === null) {
        order.push({ index: d.index, phase: pk });
      } else {
        const at = order.findIndex((x) => x.index === before && x.phase === pk);
        if (at < 0) order.push({ index: d.index, phase: pk });
        else order.splice(at, 0, { index: d.index, phase: pk });
      }
    }

    /* Nothing actually moved — do not spend a round trip on a no-op. */
    const wasOrder: { index: number; phase: string }[] = [];
    for (const key of [...new Set(groupKeys)])
      list.forEach((item, i) => {
        if (phaseOf(item) === key) wasOrder.push({ index: i, phase: key });
      });
    if (
      wasOrder.length === order.length &&
      wasOrder.every((x, i) => x.index === order[i]!.index && x.phase === order[i]!.phase)
    )
      return;

    const prevProposal = proposal;
    /* Optimistic: rewrite the proposal locally so the item moves immediately. */
    const rebuilt = order.map((x) => {
      const item = { ...list[x.index]! };
      if (s(item.phase_key) !== x.phase) {
        item.phase_key = x.phase;
        item.phase_label = PHASE_LABEL_FULL[x.phase] ?? x.phase;
        item.phase_overridden = true;
      }
      return item;
    });
    const nextProposal = JSON.parse(JSON.stringify(proposal)) as Dict;
    const npg = ((nextProposal.programs as Dict[]) ?? []).find(
      (x) => s(x.ref ?? x.code) === pid,
    );
    if (npg) {
      if (oid) ((npg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[oid] = rebuilt;
      else npg.global_deliverables = rebuilt;
      setProposal(nextProposal);
    }

    try {
      await pdApi.quickStart.reorderItems(Number(run!.id), pid, oid, order);
    } catch (e) {
      setProposal(prevProposal);
      setError(`Could not move that work item: ${(e as Error).message}`);
    }
  };

  /* ---- actions ---- */
  const analyze = async () => {
    if (!files.length) {
      setError('Please select at least one file.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      /*
       * ANALYSIS IS ASYNCHRONOUS — `analyze` returns as soon as the run row exists, typically
       * in a few milliseconds. The work continues on the server and this polls for it.
       *
       * Why it cannot simply await the analysis: AWS App Runner terminates ANY request at 120
       * seconds and exposes no setting to raise it, while a real file takes 2–5 minutes. The
       * old shape completed the work and then had nowhere to deliver it — the browser had
       * already been cut off and rendered a blank page.
       *
       * The poll has no timeout of its own. The SERVER decides when a run is stale (20
       * minutes) and reports it as failed, which is the only place that judgement can be made
       * correctly — the browser cannot tell a slow run from a dead one, and a client-side
       * deadline would abandon a run that was still working.
       */
      const r = await pdApi.quickStart.analyze(
        files,
        instructions,
        refFiles.map((f) => ({ file: f, note: refNotes[f.name] ?? '' })),
      );
      setFiles([]);
      setRefFiles([]);
      setRefNotes({});
      if (fileRef.current) fileRef.current.value = '';
      if (refRef.current) refRef.current.value = '';
      setElapsed(0);
      const startedAt = Date.now();
      const tick = window.setInterval(
        () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
        1000,
      );

      try {
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const st = await pdApi.quickStart.runStatus(r.runId);
        if (st.status === 'analyzing') continue;
        if (st.status === 'failed') {
          throw new Error(st.error || 'The analysis failed.');
        }
        break;
      }
      } finally {
        window.clearInterval(tick);
      }

      await load(r.runId);
      setNotice({ kind: 'ok', text: '✅ Analysis complete — review below, then generate.' });
    } catch (e) {
      setNotice(null);
      setError((e as Error).message);
    } finally {
      /* `busy` stays true for the whole poll, which is what keeps the button reading
         "Analyzing…" and un-clickable while the server works. */
      setBusy(false);
    }
  };

  /* Spec 5.4 — the confirm states counts, the ROUTING CONSEQUENCE, and reversibility. */
  const generate = async () => {
    const { progs, opps, dels } = counts;
    const teamCount = [...sel].filter((k) => k.startsWith('team:')).length;
    const msg =
      `Generate from ${progs} ${plural(progs, 'project', 'projects')}, ` +
      `${opps} ${plural(opps, 'opportunity', 'opportunities')} and ` +
      `${dels} ${plural(dels, 'work item', 'work items')}?\n\n` +
      `These are created UNROUTED, exactly like manually entered records. Go to Delivery ` +
      `Pathway to decide, per opportunity, whether each goes to Option A (straight to ` +
      `execution) or Option B (full planning path).\n\n` +
      (teamCount
        ? `${teamCount} team ${plural(teamCount, 'member', 'members')} will be added to the project team.\n\n`
        : '') +
      `This creates real records (you can delete them afterwards).`;
    if (!window.confirm(msg)) return;

    setBusy(true);
    setError(null);
    try {
      /*
       * THE SERVER'S SELECTION MAP IS OPT-OUT, and uses its own key shape:
       *   program      <ref>
       *   opportunity  <oref>
       *   work item    <oref>:d:<name>
       *   team member  <ref>:team:<name>
       *   global item  <ref>:global:<index>
       * Only `false` entries matter, so this sends exactly the exclusions. Translating here
       * rather than adopting the server's keys throughout keeps the cascade rules of spec
       * 5.3 expressible as composite keys.
       */
      const selection: Record<string, boolean> = {};
      for (const pg of programs) {
        const pid = s(pg.ref ?? pg.code);
        if (!sel.has(keyProg(pid))) selection[s(pg.ref)] = false;
        for (const o of (pg.opportunities as Dict[]) ?? []) {
          const oid = s(o.ref ?? o.code);
          if (!sel.has(keyOpp(pid, oid))) selection[s(o.ref)] = false;
          const dels =
            ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
          dels.forEach((d, i) => {
            if (!sel.has(keyDel(pid, oid, String(i))))
              selection[`${s(o.ref)}:d:${s(d.name)}`] = false;
          });
        }
        for (const t of (pg.team as Dict[]) ?? []) {
          if (!sel.has(keyTeam(pid, s(t.ref ?? t.name))))
            selection[`${s(pg.ref)}:team:${s(t.name)}`] = false;
        }
        /* Keyed by INDEX rather than name, because the catalogue's Global set and an analyst's
           reference list can legitimately contain two items with the same wording. */
        ((pg.global_deliverables as Dict[]) ?? []).forEach((_g, gi) => {
          if (!sel.has(keyGlobal(pid, String(gi))))
            selection[`${s(pg.ref)}:global:${gi}`] = false;
        });
      }

      const r = await pdApi.quickStart.generate({ run_id: run!.id, selection, resolutions });
      await load(Number(run!.id));
      setNotice({
        kind: 'ok',
        text:
          `✅ Generated ${r.result.projects} project(s), ${r.result.opportunities} opportunity(ies), ` +
          `${r.result.deliverables} work item(s), ${r.result.teamMembers} team member(s). ` +
          `They are UNROUTED — choose Option A or B on Delivery Pathway.`,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /* Spec 5.5 — a typed word, not an OK button. */
  const purge = async () => {
    const v = window.prompt(
      'This permanently deletes ALL records this run created (projects, opportunities, work items). Type DELETE to confirm.',
    );
    if (!v || v.trim().toUpperCase() !== 'DELETE') return;
    setBusy(true);
    try {
      await pdApi.quickStart.purge(Number(run!.id));
      await load(Number(run!.id));
      setNotice({ kind: 'purge', text: '🧹 Generated records deleted.' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleSet = <T,>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, v: T) =>
    setter((cur) => {
      const next = new Set(cur);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  if (loading) return <div className="pd-screen pd-loading">Loading…</div>;

  return (
    <div
      className="qs2-page"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="qs2-head">
        <h1 className="qs2-h1">Program Quick Start</h1>
      </div>

      <div className="qs2-body" ref={bodyRef}>
        {error ? <div className="qs2-err">{error}</div> : null}
        {notice ? <div className={`qs2-note ${notice.kind}`}>{notice.text}</div> : null}

        {/* ---- UPLOAD CARD (spec Part 3) ---- */}
        <div className="qs2-card">
          <div className="qs2-card-hd">
            <span className="qs2-tile">
              <i className="material-icons">upload_file</i>
            </span>
            <h2 className="qs2-card-title">Analyze a client file</h2>
          </div>

          <div className="qs2-drop">
            {/* Numbered 1 · and 2 · — a two-step sequence, not two unrelated fields. */}
            <div className="qs2-step">
              <label className="qs2-fld-lbl" htmlFor="qs2-file">
                1 · Choose the client file(s)
              </label>
              <input
                id="qs2-file"
                ref={fileRef}
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              />
              {files.length ? (
                <p className="qs2-files">
                  {files.length} {plural(files.length, 'file', 'files')} selected:{' '}
                  {files.map((f) => f.name).join(', ')}
                  <br />
                  <span className="qs2-mini">
                    They are analysed together and joined on shared codes.
                  </span>
                </p>
              ) : null}
            </div>

            <div className="qs2-step">
              <label className="qs2-fld-lbl" htmlFor="qs2-instr">
                2 · Analysis instructions{' '}
                <span className="qs2-fld-opt">
                  — optional, but it makes the analysis much more accurate
                </span>
              </label>
              <textarea
                id="qs2-instr"
                className="qs2-ta"
                maxLength={4000}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={
                  'Projects are on the "Initiatives" tab — the Initiative Number is the project number.\n' +
                  'Opportunities are on the "Milestones" tab; join them to a project on the Initiative Number column.\n' +
                  'Ignore the "Archive" and "Lookups" tabs.\n' +
                  'What they call a Workstream is our Project; a Deliverable is our Opportunity.'
                }
              />
              <p className="qs2-ex">
                <span>
                  Tell the analysis where things are — which tab or slide holds the projects,
                  which holds the opportunities, how they join, and what to ignore. It can
                  redirect how the file is read; it <strong>cannot</strong> invent records that
                  aren&rsquo;t in it.
                </span>
                <span className="qs2-count">{instructions.length} / 4000</span>
              </p>
            </div>

            {/*
              STEP 3 — REFERENCE FILES. Implementation spec 6.1.

              A different KIND of input from step 1, and the copy has to carry that or the
              feature is actively dangerous: these are NOT mined for projects, they TELL the
              analyzer what something is. The three behaviours stated plainly below are the
              three the user must know before attaching anything —

                * what a reference is for;
                * that its work items are used INSTEAD OF the AI's own selection;
                * that it is NEVER read as a source of projects or opportunities.

              Text-bearing only: a reference is quoted into the prompt as text, so an image
              cannot be one. The accept list is SUPPORTED_REFERENCE, not SUPPORTED.
            */}
            <div className="qs2-step">
              <label className="qs2-fld-lbl" htmlFor="qs2-ref">
                3 · Reference files{' '}
                <span className="qs2-fld-opt">
                  — optional — say what each one is, and the AI will use it as given
                </span>
              </label>
              <input
                id="qs2-ref"
                ref={refRef}
                type="file"
                multiple
                accept=".pptx,.docx,.xlsx,.pdf,.txt,.csv,.md"
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  setRefFiles(picked);
                  /* Notes are keyed BY FILENAME, matching how the server pairs them — so
                     re-picking files keeps any note already typed for a file of the same name
                     and drops the rest. */
                  setRefNotes((prev) => {
                    const next: Record<string, string> = {};
                    for (const f of picked) next[f.name] = prev[f.name] ?? '';
                    return next;
                  });
                }}
              />

              {/* One row per file: the name, and the operator's note for THAT file. This is
                  what makes filename-based pairing possible on the server. */}
              {refFiles.length ? (
                <div className="qs2-refs">
                  {refFiles.map((f) => (
                    <div className="qs2-ref-row" key={f.name}>
                      <span className="qs2-ref-name" title={f.name}>
                        {f.name}
                      </span>
                      <input
                        type="text"
                        className="qs2-ref-note"
                        maxLength={500}
                        value={refNotes[f.name] ?? ''}
                        onChange={(e) =>
                          setRefNotes((prev) => ({ ...prev, [f.name]: e.target.value }))
                        }
                        placeholder="What is this file? e.g. the approved deliverable list for project 3664 — use these, do not infer others"
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              <p className="qs2-ex">
                <span>
                  Use this for the things you already know and don&rsquo;t want the AI to work
                  out — an agreed <strong>deliverables / work-item list</strong>, an{' '}
                  <strong>action item register</strong>, a scope document. Whatever a reference
                  file states is <strong>taken as settled</strong>. If it lists an
                  opportunity&rsquo;s work items, <strong>that list is used and the AI selects
                  none of its own</strong> for that opportunity — it stops looking. The standing
                  test and go-live items are still added. Reference files are{' '}
                  <strong>never</strong> read as a source of projects or opportunities — put
                  those in step 1. Text-bearing files only —{' '}
                  <strong>Word, Excel, PowerPoint, PDF, CSV or text</strong>. Not images: a
                  reference has to be quoted to the AI as text, so screenshots belong in step 1,
                  where the AI reads them directly.
                </span>
              </p>
            </div>

            <div className="qs2-actions">
              <button type="button" className="qs2-btn" disabled={busy} onClick={analyze}>
                <i className="material-icons">
                  {busy ? 'hourglass_top' : 'auto_awesome'}
                </i>
                {busy
                  ? elapsed
                    ? `Analyzing… ${Math.floor(elapsed / 60)}m ${String(elapsed % 60).padStart(2, '0')}s`
                    : 'Analyzing…'
                  : 'Analyze & preview'}
              </button>
            </div>
          </div>
        </div>

        {/* ---- RECENT ANALYSES ---- */}
        {runs.length ? (
          <div className="qs2-card">
            <div className="qs2-card-hd">
              <span className="qs2-tile">
                <i className="material-icons">history</i>
              </span>
              <h2 className="qs2-card-title">Recent analyses</h2>
            </div>
            {runs.map((r) => {
              const id = n(r.id);
              const open = openRuns.has(id);
              const rp = (r.proposal as Dict) ?? {};
              const rprogs = (rp.programs as Dict[]) ?? [];
              const oppTotal = rprogs.reduce(
                (t, p) => t + ((p.opportunities as Dict[]) ?? []).length,
                0,
              );
              const delTotal = rprogs.reduce(
                (t, p) =>
                  t +
                  Object.values((p.deliverables_by_opp as Record<string, Dict[]>) ?? {}).reduce(
                    (x, arr) => x + arr.length,
                    0,
                  ),
                0,
              );
              return (
                <div className="qs2-run" key={id}>
                  <div className="qs2-run-hd" onClick={() => toggleSet(setOpenRuns, id)}>
                    <span className={`qs2-caret${open ? ' open' : ''}`}>
                      <i className="material-icons">chevron_right</i>
                    </span>
                    <span className="qs2-run-badge">
                      {s(r.file_type).toUpperCase().slice(0, 4) || 'FILE'}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="qs2-run-title">
                        {rprogs.length === 1
                          ? s(rprogs[0]!.name)
                          : `${rprogs.length} ${plural(rprogs.length, 'project', 'projects')}`}
                      </div>
                      <div className="qs2-mini">{s(r.file_name)}</div>
                    </div>
                    <div className="qs2-run-meta">
                      <Chip icon="folder">{rprogs.length}</Chip>
                      <Chip icon="lightbulb">{oppTotal}</Chip>
                      <Chip icon="assignment">{delTotal}</Chip>
                    </div>
                    <span className="qs2-run-when">
                      <i className="material-icons" style={{ fontSize: 14 }}>
                        schedule
                      </i>
                      {s(r.created)}
                    </span>
                    {/*
                      OPEN REVIEW — design spec's run-row order is
                      `caret · badge · title · file · chips · time · Open review · delete`.

                      Without it a past run can only be EXPANDED, which shows the tree but
                      never loads the proposal into the review pane — so a run could be
                      analysed and then never generated from, which is most of what this
                      screen is for. `load(id)` is the same call the analyse flow makes when
                      its poll completes, so a reopened run lands in exactly the state a
                      freshly analysed one does.
                    */}
                    {/*
                      A LINK, and a CHIP — the source is
                      `<a href="…?run=<id>" onclick="event.stopPropagation();"
                          class="qs-chip" style="background:#eef1ff;color:#3b5bdb;">Open review</a>`
                      (`views/roadmap-execution/quick-start.ejs:383`). Cloned: the same element,
                      the same chip treatment, the same stopPropagation so it does not also
                      toggle the row open.
                    */}
                    <a
                      className="qs2-chip qs2-run-open"
                      href={`?run=${id}`}
                      title="Open this analysis for review"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSearchParams({ run: String(id) });
                      }}
                    >
                      Open review
                    </a>
                    <button
                      type="button"
                      className="qs2-run-del"
                      title="Delete this analysis"
                      onClick={(e) => {
                        e.stopPropagation();
                        void (async () => {
                          if (!window.confirm(`Delete analysis of ${s(r.file_name)}?`)) return;
                          await pdApi.quickStart.remove(id);
                          await load();
                        })();
                      }}
                    >
                      <i className="material-icons" style={{ fontSize: 18 }}>
                        delete_outline
                      </i>
                    </button>
                  </div>

                  {open ? (
                    <div className="qs2-tree">
                      {r.instructions ? (
                        <div className="qs2-instr">
                          <b>Instructions used:</b> {s(r.instructions)}
                        </div>
                      ) : null}
                      {rprogs.map((pg, i) => (
                        <Fragment key={i}>
                          <div className="qs2-tp">
                            <span className="qs2-tp-dot" />
                            {pg.code ? <Chip kind="code">{s(pg.code)}</Chip> : null}
                            <span className="qs2-tp-name">{s(pg.name)}</span>
                          </div>
                          <div className="qs2-tos">
                            {((pg.opportunities as Dict[]) ?? []).map((o, j) => (
                              <div className="qs2-to" key={j}>
                                {o.code ? <Chip kind="code">{s(o.code)}</Chip> : null}
                                <span className="qs2-to-name">{s(o.name)}</span>
                                {o.start_date || o.end_date ? (
                                  <Chip kind="date">{dateRange(o.start_date, o.end_date)}</Chip>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </Fragment>
                      ))}
                      <button
                        type="button"
                        className="qs2-btn"
                        style={{ marginTop: 10 }}
                        onClick={() => void load(id)}
                      >
                        Open review
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {/* ---- NOTHING FOUND (spec 5.1) ---- */}
        {proposal && programs.length === 0 ? (
          <div className="qs2-note warn block">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              🔎 No projects could be read from this file.
            </div>
            <div>
              Upload it again and fill in <strong>Analysis instructions</strong> — name the tab
              or slide that holds the projects and the one that holds the opportunities. That is
              exactly what the box is for.
            </div>
          </div>
        ) : null}

        {/* ---- PROPOSAL ---- */}
        {programs.length > 0 ? (
          <>
            <div className="qs2-note">
              🔎 {programs.length} {plural(programs.length, 'project', 'projects')} detected.
            </div>

            {run?.instructions ? (
              <div className="qs2-instr">
                <b>Instructions used:</b> {s(run.instructions)}
              </div>
            ) : null}

            {/* Selection summary — the only dark element on the page. Spec 4.6. */}
            <div className="qs2-summary">
              <span>
                Selected: <b>{counts.progs}</b>{' '}
                {plural(counts.progs, 'project', 'projects')} · <b>{counts.opps}</b>{' '}
                {plural(counts.opps, 'opportunity', 'opportunities')} · <b>{counts.dels}</b>{' '}
                {plural(counts.dels, 'work item', 'work items')}
              </span>

              {/* Order is deliberate — spec 6.3: counts → filter → (gap) → Select all →
                  Clear all → Generate. The filter is what you reach for first on a 90-project
                  intake; the three actions belong together at the end. */}
              <span className={`qs2-find${find ? ' on' : ''}`}>
                <i className="material-icons">search</i>
                <input
                  type="text"
                  value={find}
                  placeholder="Project or opportunity — exact ID, or part of a name"
                  autoComplete="off"
                  onChange={(e) => setFind(e.target.value)}
                />
                {find ? (
                  <button
                    type="button"
                    className="qs2-find-x"
                    title="Clear filter"
                    onClick={() => setFind('')}
                  >
                    ×
                  </button>
                ) : null}
              </span>
              {find ? (
                <span className="qs2-find-note">
                  {visible.progs.size} {plural(visible.progs.size, 'project', 'projects')} ·{' '}
                  {visible.opps.size}{' '}
                  {plural(visible.opps.size, 'opportunity', 'opportunities')}
                </span>
              ) : null}

              {/*
                §19.2 — the action bar is NOT swapped out once a run has generated. It used
                to be: Select all / Clear all / Generate were replaced wholesale by a chip and
                a Delete button, which is the run-level lock again. "A new initiative under a
                generated project is still selectable" means nothing without a Generate button
                to press afterwards.

                Generated records travel in the selection, disabled and ticked, exactly as the
                source sends them. Re-generating is safe because §19.3 recomputes the duplicate
                warnings, so anything that now exists is flagged and defaults to merge rather
                than being created twice.
              */}
              <button
                type="button"
                className="qs2-btn-ghost"
                onClick={() => selectAll(true)}
                title={
                  find ? 'Selects only what the filter is showing' : 'Selects everything on screen'
                }
              >
                Select all
              </button>
              <button type="button" className="qs2-btn-ghost" onClick={() => selectAll(false)}>
                Clear all
              </button>
              <span className="qs2-spacer" />
              {anyGenerated ? (
                <>
                  <span className="qs2-gen-chip">✓ Records generated</span>
                  <button type="button" className="qs2-btn-del" disabled={busy} onClick={purge}>
                    Delete generated data
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className="qs2-btn-gen"
                disabled={busy || sel.size === 0}
                onClick={generate}
              >
                <i className="material-icons" style={{ fontSize: 16 }}>
                  rocket_launch
                </i>
                Generate selected
              </button>
            </div>

            {/* THE PROPOSAL TREE — one component, used by this screen and by Quick Start
                Analysis, because the source renders both bodies from one partial
                (`include('_quick-start-review')`). See QuickStartReviewBody.tsx. */}
            <QuickStartReviewBody
              programs={programs}
              proposal={proposal}
              visible={visible}
              genProg={genProg}
              genOpp={genOpp}
              isOn={isOn}
              setMany={setMany}
              toggleProgram={toggleProgram}
              toggleOpp={toggleOpp}
              toggleDel={toggleDel}
              dupWarn={dupWarn}
              resolutions={resolutions}
              setResolutions={setResolutions}
              dropHint={dropHint}
              dropBefore={dropBefore}
              onItemDragStart={onItemDragStart}
              onItemDragEnd={onItemDragEnd}
              onPhaseDragOver={onPhaseDragOver}
              onPhaseDrop={onPhaseDrop}
            />

            {assumptions.length ? (
              <div className="qs2-assump">
                <p className="qs2-assump-hd">Assumptions</p>
                <ul>
                  {assumptions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
